import { Alert, Box, Modal, Typography } from "@mui/material";
import Button from "../../../shared/OAWorkflow/components/atoms/Button";
import UploadPanel from "./components/UploadPanel";
import UploadSummary from "./components/UploadSummary";
import { useInventorImport } from "./useInventorImport";

/**
 * Bulk upload of inventor details (KOT-101), started from a client's Inventor Information
 * section. Every imported inventor is attached to that client — the template carries no
 * client column and the Client No. shown in the validation result is derived from the
 * profile, not from anything the user typed (KOT-103 AC10).
 *
 * One request does the whole job: the server validates, imports what it can, and returns
 * the counts. Rows that only partly validated are still imported, minus the values it
 * could not accept; the downloadable validation result explains every omission.
 *
 * Closing is blocked while an import is in flight — the request is what creates the
 * inventors, so tearing the modal down mid-flight would leave the grid behind the server.
 */
const InventorBulkUploadModal = ({ open, onClose, clientProfileId, clientNumber, onImported }) => {
  const importer = useInventorImport({ clientProfileId, onImported });

  const handleClose = () => {
    if (importer.uploading) return;
    importer.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ opacity: 1, mx: "auto", display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Box
        bgcolor="#fff"
        width={860}
        maxWidth="95vw"
        borderRadius={2}
        p={4}
        sx={{ maxHeight: "90vh", overflowY: "auto" }}
        data-testid="inventor-bulk-upload-modal"
      >
        <Typography variant="h3" mb={1}>
          Bulk Upload Inventors
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>
          {clientNumber ? ` Client No. ${clientNumber}` : ""}
        </Typography>

        {clientProfileId ? (
          <>
            <UploadPanel
              file={importer.file}
              onFileSelected={importer.selectAndUpload}
              onFileRejected={importer.rejectFile}
              onClearFile={importer.clearFile}
              onDownloadTemplate={importer.fetchTemplate}
              uploading={importer.uploading}
              downloadingTemplate={importer.downloadingTemplate}
              error={importer.uploadError}
            />

            {importer.summary && (
              <UploadSummary
                summary={importer.summary}
                onDownloadResult={importer.fetchResult}
                downloadingResult={importer.downloadingBatchId === importer.summary.batchId}
                downloadError={importer.downloadError}
              />
            )}
          </>
        ) : (
          // Reached only if the modal is opened before the client profile has loaded.
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            This client profile is still loading. Close this window and try again.
          </Alert>
        )}

        <Box mt={3} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
          <Button variant="secondary" onClick={handleClose} disabled={importer.uploading}>
            {importer.summary ? "Done" : "Cancel"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default InventorBulkUploadModal;
